import React, { useState, useEffect, useCallback } from "react";
import { apiService } from '../../../components/Config/api.service';
import EmployeesPrime from './EmployeesPrime';
import { notify } from "../../../components/Utils/Notifications";
import { FaArrowLeft, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import { FiTrash2 } from 'react-icons/fi';
import ActionDropdown from '../../../components/Utils/ActionDropdown';
import Modal from '../../../components/Utils/ModalShow';

const BasePrimeForm = ({ 
    id, 
    prime, 
    setPrime, 
    closeEdit, 
    title, 
    children, 
    onBeforeSave, 
    apiEndpoint = "/prime", // Endpoint par défaut pour les primes
    type = "prime" // Type par défaut
}) => {
    const [loading, setLoading] = useState(false);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [isAccountSectionExpanded, setIsAccountSectionExpanded] = useState(false);
    const [isEmployeeSectionExpanded, setIsEmployeeSectionExpanded] = useState(true);
    const [employeesPrimeChanges, setEmployeesPrimeChanges] = useState({
        employeesPrimes: [],
        newEmployeesPrimes: [],
        modifiedEmployeesPrimes: [],
        removedEmployeesPrimes: []
    });

    const handleEmployeesPrimesChange = useCallback((changes) => {
        setEmployeesPrimeChanges(changes);
    }, []);

    useEffect(() => {
        if (id) {
            setLoading(true);
            apiService.getElementById(apiEndpoint, id)
                .then(response => {
                    const dataKey = type === "advantage" ? "advantage" : "prime";
                    setPrime(response.data[dataKey]);
                })
                .catch(() => notify('Erreur lors de la récupération des données.', '', 3))
                .finally(() => setLoading(false));
        }
    }, [id, setPrime, apiEndpoint, type]);

    const saveEmployeePrimeChanges = async (primeId) => {
        const { newEmployeesPrimes, modifiedEmployeesPrimes, removedEmployeesPrimes } = employeesPrimeChanges;
        if (newEmployeesPrimes.length === 0 && modifiedEmployeesPrimes.length === 0 && removedEmployeesPrimes.length === 0) {
            return; // Rien à sauvegarder
        }
        try {
            const employeePrimeData = {
                newEmployees: newEmployeesPrimes,
                modifiedEmployees: modifiedEmployeesPrimes,
                removedEmployees: removedEmployeesPrimes
            };
            await apiService.saveElement(`${apiEndpoint}/${primeId}/employees`, employeePrimeData);
            notify('Employés affectés mis à jour avec succès', '', 2);
        } catch (error) {
            notify('Erreur lors de la mise à jour des employés affectés', '', 3);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const primeToSave = { ...prime };
        if (onBeforeSave) {
            onBeforeSave(primeToSave);
        }

        try {
            if (id) {
                await apiService.updateElementById(apiEndpoint, id, primeToSave);
                await saveEmployeePrimeChanges(id);
                notify('Mise à jour réussie !', '', 2);
            } else {
                const response = await apiService.saveElement(apiEndpoint, primeToSave);
                const responseKey = type === "advantage" ? "advantage" : "prime";
                const newPrimeId = response.data[responseKey];
                await saveEmployeePrimeChanges(newPrimeId);
                notify('Création réussie !', '', 2);
            }
            closeEdit();
        } catch (error) {
            if (error.response?.data?.error === 'exist_already') {
                notify('Cet avantage existe déjà pour cette société', '', 3);
            } else {
                notify(error.response?.data?.error || 'Une erreur est survenue.', '', 3);
            }
        } finally {
            setLoading(false);
        }
    };

    const confirmArchive = async () => {
        try {
            await apiService.updateElementById(apiEndpoint, id, { active: false });
            notify('Archivage réussi', '', 2);
            setIsArchiveModalOpen(false);
            closeEdit();
        } catch (error) {
            notify('Erreur lors de l\'archivage.', '', 3);
            setIsArchiveModalOpen(false);
        }
    };

    const dropdownActions = [{
        label: 'Archiver',
        onClick: () => setIsArchiveModalOpen(true),
        disabled: loading || !id,
        color: 'text-red-500',
        icon: <FiTrash2 size={16} />
    }];

    // Adapter le texte selon le type
    const entityName = type === "advantage" ? "avantage" : "prime";
    const EntityNameCapitalized = type === "advantage" ? "Avantage" : "Prime";

    return (
        <>
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className='flex justify-between items-center mb-6'>
                    <h1 className="text-2xl font-bold flex items-center">
                        <small className="mr-4 text-gray-400 cursor-pointer" onClick={closeEdit}><FaArrowLeft /></small>
                        {title}
                    </h1>
                    <div>
                        <button type='button' className='bg-[--color-orange] text-white rounded-full btn mx-2' onClick={closeEdit}>Annuler</button>
                        <button type="submit" className='bg-[--color-primary] text-white hover:bg-[--bg-hover] rounded-full btn' disabled={loading}>
                            {loading ? <ClipLoader color='white' size={20} /> : 'Enregistrer'}
                        </button>
                        <ActionDropdown actions={dropdownActions} />
                    </div>
                </div>

                {/* Section Informations générales */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="border-b border-gray-200 px-6 py-4">
                        <h3 className="text-lg font-semibold text-gray-800">Informations générales</h3>
                        <p className="text-sm text-gray-600 mt-1">Détails de base {type === "advantage" ? "de l'avantage" : "de la prime"}</p>
                    </div>
                    <div className="p-6">
                        {children}
                    </div>
                </div>            

                {/* Section Gestion des employés */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div 
                        className="border-b border-gray-200 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setIsEmployeeSectionExpanded(!isEmployeeSectionExpanded)}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">Gestion des employés</h3>
                                <p className="text-sm text-gray-600 mt-1">Attribution et paramètres des employés</p>
                            </div>
                            <div className="text-gray-400">
                                {isEmployeeSectionExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </div>
                    </div>
                    {isEmployeeSectionExpanded && (
                        <div className="p-6 space-y-6">
                            <div className="flex items-center justify-between p-4 bg-[--bg-color] rounded-lg border border-[--border-color]">
                                <div>
                                    <h4 className="font-medium text-[--color-primary]">Mode d'application</h4>
                                    <p className="text-sm mt-1">Choisir si {entityName === "advantage" ? "l'avantage" : "la prime"} s'applique uniquement aux employées sélectionnées</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <span className="mr-3 text-sm font-medium text-gray-700">
                                        {prime.apply_selected ? 'Sélection' : 'Tous'}
                                    </span>
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        name="apply_selected"
                                        checked={!!prime.apply_selected}
                                        onChange={e => setPrime(p => ({ ...p, apply_selected: e.target.checked }))}
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[--color-primary]"></div>
                                </label>
                            </div>
                            
                            <EmployeesPrime 
                                primeId={id} 
                                onEmployeesPrimesChange={handleEmployeesPrimesChange} 
                                saveChanges={() => saveEmployeePrimeChanges(id)}
                                apiEndpoint={apiEndpoint} // Passer l'endpoint
                                type={type} // Passer le type
                            />
                        </div>
                    )}
                </div>

                {/* Section Configuration comptable */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div 
                        className="border-b border-gray-200 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setIsAccountSectionExpanded(!isAccountSectionExpanded)}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">Configuration comptable</h3>
                                <p className="text-sm text-gray-600 mt-1">Paramètres comptables et calculs</p>
                            </div>
                            <div className="text-gray-400">
                                {isAccountSectionExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </div>
                    </div>
                    {isAccountSectionExpanded && (
                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Compte de Débit
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                        name="account_debit"
                                        value={prime.account_debit || ''}
                                        onChange={e => setPrime(p => ({ ...p, account_debit: e.target.value }))}
                                        placeholder="Compte de débit"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Compte de Crédit
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                        name="account_credit"
                                        value={prime.account_credit || ''}
                                        onChange={e => setPrime(p => ({ ...p, account_credit: e.target.value }))}
                                        placeholder="Compte de crédit"
                                    />
                                </div>
                            </div>
                            
                            <div className="border-t border-gray-200 pt-6">
                                <h4 className="text-md font-medium text-gray-800 mb-4">Options de calcul</h4>
                                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div>
                                        <h5 className="font-medium text-gray-800">Non Computé dans le Net</h5>
                                        <p className="text-sm text-gray-600 mt-1">Cet {entityName} ne sera pas inclus dans le calcul du salaire net</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            name="not_computed_in_net"
                                            checked={!!prime.not_computed_in_net}
                                            onChange={e => setPrime(p => ({ ...p, not_computed_in_net: e.target.checked }))}
                                        />
                                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </form>
            
            <Modal
                isOpen={isArchiveModalOpen}
                onClose={() => setIsArchiveModalOpen(false)}
                title="Confirmation d'archivage"
                footer={
                    <>
                        <button className="btn" onClick={() => setIsArchiveModalOpen(false)}>Annuler</button>
                        <button className="btn btn-error" onClick={confirmArchive}>Confirmer</button>
                    </>
                }
            >
                <p>Êtes-vous sûr de vouloir archiver cet {entityName} ?</p>
            </Modal>
        </>
    );
};

export default BasePrimeForm;